import css from "./button.scss?inline";
import globalStyles from "../../index.scss?inline";
import { ArgSpecDictionary, TypedVars } from "../component-utils";

import { HHDSButtonMode, HHDSButtonType, HHDSButtonWidth } from "./ButtonType";
import HHDSButtonUtils, { HHDSButtonHasRegisterPropertyClass } from "./ButtonUtils";

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = "HHDSButton";
const TAG_NAME: string = "hhds-button";
export const HHDSButtonTagName: string = "hhds-button";

// ////////////////////////////////////////////////////////////////////

export class HHDSButton extends HTMLElement {
  private vars: TypedVars = new TypedVars(this);
  private shadow: ShadowRoot;

  private supportsRegisterProperty = window.CSS && typeof window.CSS.registerProperty === "function";

  private listenerFuncs: { [key: string]: any } = {};

  constructor() {
    super();
    DEBUG_VERBOSE && console.log(CLASS_NAME, "constructed");

    this.shadow = this.attachShadow({ mode: "open" });
    if (!this.shadow) {
      return;
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    if (this.supportsRegisterProperty === false) return;

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    HHDSButtonUtils.addCustomProperty();
  }

  static get observedAttributes() {
    return Object.keys(ArgSpecs);
  }

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Lifecycle Methods
  // https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements#using_the_lifecycle_callbacks

  // Invoked each time the custom element is appended into a document-connected element.
  connectedCallback() {
    this.parseAttributes();
    this.render();
    this.addButtonEvents();
    DEBUG_VERBOSE && console.log(`[${TAG_NAME}] Initialised`);
  }

  // ////////////////////////////////////////////////////////////////////

  // ////////////////////////////////////////////////////////////////////

  private addButtonEvents(): void {
    const targetEl = this.interactionTargetElement;

    if (!targetEl) return; // /////////////////////////////////////////////////

    const href = this.vars.get<string>("href");
    const target = this.vars.get<string>("target");
    const download = this.getAttribute("download");

    DEBUG_VERBOSE &&
      console.log(CLASS_NAME, this.addButtonEvents.name, {
        href,
        target,
        download,
      });

    // Define callbacks for events separately rather than inline
    // in addEventListener, so they can be removed later.
    this.listenerFuncs["click"] = (event: PointerEvent) => {
      event.preventDefault();

      if (download && download.length > 0) {
        fetch(download, {
          method: "get",
          mode: "no-cors",
          referrerPolicy: "no-referrer",
        })
          .then((res) => res.blob())
          .then((res) => {
            const aElement = document.createElement("a");
            aElement.setAttribute("download", "file");
            const href = URL.createObjectURL(res);
            aElement.href = href;
            aElement.setAttribute("target", "_blank");
            aElement.click();
            URL.revokeObjectURL(href);
          });
      }

      if (href?.length > 0) {
        switch (target) {
          case "blank":
          case "_blank":
            window.open(href, "_blank");
            break;

          case "self":
          case "_self":
          default:
            window.location.href = href;
        }
      }

      this.emitEvent(HHDSButtonEvent.click);
    };
    this.listenerFuncs["pointerdown"] = (event: Event) => {
      //event.stopPropagation();
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.down);
    };
    this.listenerFuncs["pointerup"] = (event: Event) => {
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.up);
    };
    this.listenerFuncs["pointerenter"] = (event: Event) => {
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.over);
    };
    this.listenerFuncs["pointerleave"] = (event: Event) => {
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.out);
    };
    this.listenerFuncs["focus"] = (event: Event) => {
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.focus);
    };
    this.listenerFuncs["blur"] = (event: Event) => {
      event.preventDefault();
      this.emitEvent(HHDSButtonEvent.unfocus);
    };

    // Add all event listeners, pointing to their respective callback functions.
    // This approach allows the events to be cleanly removed later.
    const events = Object.keys(this.listenerFuncs);
    events.forEach((eventName) => targetEl?.addEventListener(eventName, this.listenerFuncs[eventName]));

    // Alternative, simpler layout:
    // buttonEl?.addEventListener("click", this.listenerFuncs["click"]);
    // buttonEl?.addEventListener("pointerdown", this.listenerFuncs["pointerdown"]);
    // buttonEl?.addEventListener("pointerup", this.listenerFuncs["pointerup"]);
    // buttonEl?.addEventListener("pointerenter", this.listenerFuncs["pointerenter"]);
    // buttonEl?.addEventListener("pointerleave", this.listenerFuncs["pointerleave"]);
    // buttonEl?.addEventListener("focus", this.listenerFuncs["focus"]);
  }

  get tabbableElement(): HTMLElement | null {
    return this.shadow.querySelector("button");
  }

  get interactionTargetElement(): HTMLElement | null {
    return this.shadow.querySelector(".hhds-button--wrapper");
  }

  private removeButtonEvents(): void {
    const targetEl = this.interactionTargetElement;
    if (!targetEl) return;
    const events = Object.keys(this.listenerFuncs);
    events.forEach((eventName) => targetEl?.removeEventListener(eventName, this.listenerFuncs[eventName]));
  }

  get buttonElement(): HTMLButtonElement | null {
    return this.shadow.querySelector("button");
  }

  parseAttributes() {
    this.vars.parse(this, ArgSpecs);
  }

  render() {
    const type = this.vars.get<HHDSButtonType>("type"),
      label = this.vars.get<string>("label"),
      disabled = this.vars.get<boolean>("disabled"),
      role = this.vars.get<string>("role"),
      mode = this.vars.get<string>("mode") ?? HHDSButtonMode.Light;

    const fontClasses = this.vars.get<string>("font-classes");

    this.shadow.innerHTML = /*html*/ `
        <style>${globalStyles}</style>
        <style>${css}</style>
        <style>
        :host {
            ${this.modeCSS}
        }
        </style>
        <div class="hhds-button--wrapper hhds-button--${mode} ${type == HHDSButtonType.Close ? `hhds-button--${type}` : ``} ${this.supportsRegisterProperty && HHDSButtonHasRegisterPropertyClass}">
              ${type == HHDSButtonType.Close ? `<svg class="hhds-button__close" width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="35" height="35" stroke="#E0E0E0"/><path d="M22.854 22.146C22.9005 22.1925 22.9373 22.2476 22.9625 22.3083C22.9876 22.369 23.0006 22.4341 23.0006 22.4998C23.0006 22.5655 22.9876 22.6305 22.9625 22.6912C22.9373 22.7519 22.9005 22.8071 22.854 22.8535C22.8076 22.9 22.7524 22.9368 22.6917 22.962C22.631 22.9871 22.566 23.0001 22.5003 23.0001C22.4346 23.0001 22.3695 22.9871 22.3088 22.962C22.2481 22.9368 22.193 22.9 22.1465 22.8535L18.0003 18.7067L13.854 22.8535C13.7602 22.9474 13.633 23.0001 13.5003 23.0001C13.3676 23.0001 13.2403 22.9474 13.1465 22.8535C13.0527 22.7597 13 22.6325 13 22.4998C13 22.3671 13.0527 22.2399 13.1465 22.146L17.2934 17.9998L13.1465 13.8535C13.0527 13.7597 13 13.6325 13 13.4998C13 13.3671 13.0527 13.2399 13.1465 13.146C13.2403 13.0522 13.3676 12.9995 13.5003 12.9995C13.633 12.9995 13.7602 13.0522 13.854 13.146L18.0003 17.2929L22.1465 13.146C22.2403 13.0522 22.3676 12.9995 22.5003 12.9995C22.633 12.9995 22.7602 13.0522 22.854 13.146C22.9478 13.2399 23.0006 13.3671 23.0006 13.4998C23.0006 13.6325 22.9478 13.7597 22.854 13.8535L18.7072 17.9998L22.854 22.146Z" fill="black"/></svg>` : ``}

            <slot class="hhds-button--icon hhds-button--icon_start" name="start"></slot>
            <button class="hhds-button hhds-button--${type} ${disabled && `hhds-button--${type}--disabled`} ${fontClasses}" aria-label="${label}" ${role && `aria-role="${role}" `}>
            ${label}<slot></slot>
            </button>
            <slot class="hhds-button--icon hhds-button--icon_end" name="end"></slot>
        </div>
		`;

    const button = this.shadow.querySelector("button")!;
    const wrapper = this.shadow.querySelector(".hhds-button--wrapper")!;
    const focusClass = `hhds-button--wrapper--focused`;
    button.addEventListener("focus", () => wrapper.classList.add(focusClass));
    button.addEventListener("blur", () => wrapper.classList.remove(focusClass));

    this.setStartIcon();
    this.setEndIcon();
  }

  get modeCSS() {
    let css = "";

    if (this.vars.get<HHDSButtonMode>("mode") === HHDSButtonMode.Dark) {
      css += `--button-color: var(--color-neutral-white); --icon-color: var(--color-icon-darkmode); `;
    } else {
      css += `--button-color: var(--color-neutral-grey-1000); --icon-color: var(--color-icon-lightmode); `;
    }

    if (this.vars.get<HHDSButtonWidth>("width") === HHDSButtonWidth.Full) {
      css += `--button-min-width: 100%;`;
    }

    return css;
  }

  setStartIcon() {
    const startSlot = this.shadow.querySelector('slot[name="start"]') as HTMLSlotElement;
    const showStart = startSlot.assignedElements().length > 0;
    if (!showStart) startSlot.remove();
  }

  setEndIcon() {
    const endSlot = this.shadow.querySelector('slot[name="end"]') as HTMLSlotElement;
    const showEnd = endSlot.assignedElements().length > 0;
    if (!showEnd) endSlot.remove();
  }

  // Invoked each time the custom element is disconnected from the document's DOM.
  disconnectedCallback() {
    this.removeButtonEvents();
  }

  // Invoked each time the custom element is moved to a new document.
  adoptedCallback() {}

  // Invoked each time one of the custom element's attributes is added, removed, or changed.
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    DEBUG_VERBOSE && console.log(`Attribute ${name} has changed from ${oldValue} to ${newValue}.`);
    this.removeButtonEvents();
    this.parseAttributes();
    this.render();
    this.addButtonEvents();
  }

  private emitEvent(eventName: string, data: any = null) {
    const event = new CustomEvent(eventName, { detail: data });
    this.dispatchEvent(event);
  }
}

export enum HHDSButtonEvent {
  click = "hhds-button-click",
  over = "hhds-button-over",
  out = "hhds-button-out",
  down = "hhds-button-down",
  up = "hhds-button-up",
  focus = "hhds-button-focus",
  unfocus = "hhds-button-unfocus",
}

export const ArgSpecs: ArgSpecDictionary = {
  href: {
    description: "Navigate to this URL on click",
    defaultValue: "",
    type: String,
  },
  target: {
    description: "Target for the link",
    defaultValue: "_self",
    type: String,
  },
  type: {
    description: `Type of button: "primary", "secondary", "overlay".`,
    defaultValue: HHDSButtonType.Primary,
    type: HHDSButtonType,
  },
  mode: {
    description: `Button mode: "Light", "Dark"`,
    defaultValue: HHDSButtonMode.Light,
    type: HHDSButtonMode,
  },
  width: {
    description: "Width of button",
    defaultValue: HHDSButtonWidth.Default,
    type: HHDSButtonWidth,
  },
  label: {
    description: "Text label for the button, can be multi-line.",
    defaultValue: "",
    type: String,
  },
  role: {
    description: "Role of the button",
    defaultValue: "",
    type: String,
  },
  disabled: {
    description: "Disabled state of button",
    defaultValue: "false",
    type: Boolean,
  },
  "font-classes": {
    description: "Font classes",
    defaultValue: "uppercase uppercase--01",
    type: String,
  },
};
